/**
 * Log Collector
 *
 * Automatically captures client-side errors and warnings:
 * - console.error() and console.warn()
 * - window.onerror (unhandled JS errors)
 * - unhandledrejection (promise rejections)
 *
 * Features:
 * - Batching: Accumulates logs and sends every 30 seconds or when batch is full
 * - Flush on beforeunload: Ensures logs are sent before page closes
 * - Deduplication: Avoids duplicate logs within a short time window
 * - Rate limiting: Prevents flooding the backend
 */

import type { CreateClientLogInput, LogLevel, LogSource } from '../domain/support';
import { createClientLogsBatch } from '../services/supportService';

// =============================================================================
// CONFIGURATION
// =============================================================================

const CONFIG = {
  /** Max logs in a batch before auto-sending */
  BATCH_SIZE: 10,
  /** Send interval in milliseconds (30 seconds) */
  SEND_INTERVAL: 30000,
  /** Deduplication window in milliseconds (5 seconds) */
  DEDUPE_WINDOW: 5000,
  /** Max logs per minute (rate limiting) */
  MAX_LOGS_PER_MINUTE: 20,
  /** Ignore logs containing these strings */
  IGNORE_PATTERNS: [
    'ResizeObserver loop',
    'Loading module',
    '[HMR]',
    '[vite]',
    'Download the React DevTools',
  ],
};

// =============================================================================
// STATE
// =============================================================================

interface LogCollectorState {
  initialized: boolean;
  userId: string | null;
  accountId: string | null;
  batch: CreateClientLogInput[];
  recentHashes: Map<string, number>; // hash -> timestamp
  logsThisMinute: number;
  minuteStart: number;
  intervalId: ReturnType<typeof setInterval> | null;
  originalConsoleError: typeof console.error | null;
  originalConsoleWarn: typeof console.warn | null;
}

const state: LogCollectorState = {
  initialized: false,
  userId: null,
  accountId: null,
  batch: [],
  recentHashes: new Map(),
  logsThisMinute: 0,
  minuteStart: Date.now(),
  intervalId: null,
  originalConsoleError: null,
  originalConsoleWarn: null,
};

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Generate a simple hash for deduplication
 */
function hashLog(message: string, source: LogSource): string {
  return `${source}:${message.slice(0, 100)}`;
}

/**
 * Check if a message should be ignored
 */
function shouldIgnore(message: string): boolean {
  return CONFIG.IGNORE_PATTERNS.some((pattern) => message.includes(pattern));
}

/**
 * Check rate limiting
 */
function isRateLimited(): boolean {
  const now = Date.now();

  // Reset counter every minute
  if (now - state.minuteStart > 60000) {
    state.minuteStart = now;
    state.logsThisMinute = 0;
  }

  return state.logsThisMinute >= CONFIG.MAX_LOGS_PER_MINUTE;
}

/**
 * Check for duplicate within dedupe window
 */
function isDuplicate(hash: string): boolean {
  const now = Date.now();

  // Clean old entries
  for (const [key, timestamp] of state.recentHashes) {
    if (now - timestamp > CONFIG.DEDUPE_WINDOW) {
      state.recentHashes.delete(key);
    }
  }

  if (state.recentHashes.has(hash)) {
    return true;
  }

  state.recentHashes.set(hash, now);
  return false;
}

/**
 * Extract stack trace from error
 */
function getStack(error: unknown): string | undefined {
  if (error instanceof Error && error.stack) {
    return error.stack;
  }
  return undefined;
}

/**
 * Format arguments to a message string
 */
function formatArgs(args: unknown[]): string {
  return args
    .map((arg) => {
      if (typeof arg === 'string') return arg;
      if (arg instanceof Error) return arg.message;
      try {
        return JSON.stringify(arg);
      } catch {
        return String(arg);
      }
    })
    .join(' ');
}

// =============================================================================
// CORE FUNCTIONS
// =============================================================================

/**
 * Add a log entry to the batch
 */
function addLog(
  level: LogLevel,
  source: LogSource,
  message: string,
  stack?: string,
  metadata?: Record<string, unknown>
): void {
  // Must be initialized with user info
  if (!state.initialized || !state.userId || !state.accountId) {
    return;
  }

  // Check ignore patterns
  if (shouldIgnore(message)) {
    return;
  }

  // Check rate limiting
  if (isRateLimited()) {
    return;
  }

  // Check deduplication
  const hash = hashLog(message, source);
  if (isDuplicate(hash)) {
    return;
  }

  // Add to batch
  state.logsThisMinute++;
  state.batch.push({
    level,
    source,
    message: message.slice(0, 2000), // Limit message length
    stack: stack?.slice(0, 5000), // Limit stack length
    url: typeof window !== 'undefined' ? window.location.href : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    metadata,
  });

  // Send immediately if batch is full
  if (state.batch.length >= CONFIG.BATCH_SIZE) {
    flushLogs();
  }
}

/**
 * Send accumulated logs to the backend
 */
async function flushLogs(): Promise<void> {
  if (state.batch.length === 0) {
    return;
  }

  const logsToSend = [...state.batch];
  state.batch = [];

  try {
    await createClientLogsBatch(logsToSend);
  } catch (error) {
    // Re-add logs to batch on failure (will retry on next flush)
    // But only if not too many, to prevent memory issues
    if (state.batch.length < CONFIG.BATCH_SIZE * 2) {
      state.batch.push(...logsToSend);
    }
    // Don't log the error to console to avoid infinite loops
  }
}

// =============================================================================
// INTERCEPTORS
// =============================================================================

/**
 * Intercept console.error
 */
function interceptConsoleError(): void {
  if (typeof console === 'undefined') return;

  state.originalConsoleError = console.error;
  console.error = (...args: unknown[]) => {
    // Call original
    state.originalConsoleError?.apply(console, args);

    // Capture
    const message = formatArgs(args);
    const stack = args.find((a) => a instanceof Error);
    addLog('error', 'console', message, getStack(stack));
  };
}

/**
 * Intercept console.warn
 */
function interceptConsoleWarn(): void {
  if (typeof console === 'undefined') return;

  state.originalConsoleWarn = console.warn;
  console.warn = (...args: unknown[]) => {
    // Call original
    state.originalConsoleWarn?.apply(console, args);

    // Capture
    const message = formatArgs(args);
    addLog('warn', 'console', message);
  };
}

/**
 * Intercept window.onerror (unhandled errors)
 */
function interceptWindowError(): void {
  if (typeof window === 'undefined') return;

  window.onerror = (message, source, lineno, colno, error) => {
    const msg = typeof message === 'string' ? message : 'Unknown error';
    const stack = error?.stack || `at ${source}:${lineno}:${colno}`;

    addLog('error', 'unhandled', msg, stack, {
      source,
      lineno,
      colno,
    });

    // Don't prevent default handling
    return false;
  };
}

/**
 * Intercept unhandled promise rejections
 */
function interceptUnhandledRejection(): void {
  if (typeof window === 'undefined') return;

  window.onunhandledrejection = (event) => {
    const reason = event.reason;
    let message = 'Unhandled promise rejection';
    let stack: string | undefined;

    if (reason instanceof Error) {
      message = reason.message;
      stack = reason.stack;
    } else if (typeof reason === 'string') {
      message = reason;
    } else {
      try {
        message = JSON.stringify(reason);
      } catch {
        message = String(reason);
      }
    }

    addLog('error', 'promise_rejection', message, stack);
  };
}

/**
 * Flush logs before page unload
 */
function interceptBeforeUnload(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('beforeunload', () => {
    // Use sendBeacon for reliable delivery during unload
    if (state.batch.length > 0 && navigator.sendBeacon) {
      // For now, just flush synchronously
      // In production, you might want to use sendBeacon with a dedicated endpoint
      flushLogs();
    }
  });
}

// =============================================================================
// PUBLIC API
// =============================================================================

/**
 * Initialize the log collector
 *
 * Call this after GraphQL is initialized and user is authenticated.
 *
 * @param getUserId - Function that returns the current user ID
 * @param getAccountId - Function that returns the current account ID
 */
export function initLogCollector(
  getUserId: () => string | null,
  getAccountId: () => string | null
): void {
  if (state.initialized) {
    console.warn('[LogCollector] Already initialized');
    return;
  }

  // Store getters for dynamic user/account resolution
  const updateUserInfo = () => {
    state.userId = getUserId();
    state.accountId = getAccountId();
  };

  // Initial update
  updateUserInfo();

  // Set up interceptors
  interceptConsoleError();
  interceptConsoleWarn();
  interceptWindowError();
  interceptUnhandledRejection();
  interceptBeforeUnload();

  // Set up periodic flush
  state.intervalId = setInterval(() => {
    updateUserInfo(); // Refresh user info in case it changed
    flushLogs();
  }, CONFIG.SEND_INTERVAL);

  state.initialized = true;
  console.log('[LogCollector] Initialized');
}

/**
 * Stop the log collector and restore original console methods
 */
export function stopLogCollector(): void {
  if (!state.initialized) return;

  // Restore console methods
  if (state.originalConsoleError) {
    console.error = state.originalConsoleError;
  }
  if (state.originalConsoleWarn) {
    console.warn = state.originalConsoleWarn;
  }

  // Clear interval
  if (state.intervalId) {
    clearInterval(state.intervalId);
    state.intervalId = null;
  }

  // Flush remaining logs
  flushLogs();

  // Reset state
  state.initialized = false;
  state.userId = null;
  state.accountId = null;
  state.batch = [];
  state.recentHashes.clear();

  console.log('[LogCollector] Stopped');
}

/**
 * Manually log an error (e.g., from React Error Boundary)
 */
export function logError(
  message: string,
  stack?: string,
  metadata?: Record<string, unknown>
): void {
  addLog('error', 'boundary', message, stack, metadata);
}

/**
 * Manually log a warning
 */
export function logWarning(
  message: string,
  metadata?: Record<string, unknown>
): void {
  addLog('warn', 'boundary', message, undefined, metadata);
}

/**
 * Manually log info (for important events)
 */
export function logInfo(
  message: string,
  metadata?: Record<string, unknown>
): void {
  addLog('info', 'console', message, undefined, metadata);
}

/**
 * Force flush all pending logs immediately
 */
export function flushLogsNow(): Promise<void> {
  return flushLogs();
}

/**
 * Check if log collector is initialized
 */
export function isLogCollectorInitialized(): boolean {
  return state.initialized;
}
